import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import Landing from "@/pages/Landing";
import GuestLogin from "@/pages/GuestLogin";
import AuthCallback from "@/pages/AuthCallback";
import AppLayout from "@/components/AppLayout";
import CookieBanner from "@/components/CookieBanner";
import Privacy from "@/pages/Privacy";
import Termini from "@/pages/Termini";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import Sales from "@/pages/Sales";
import Analytics from "@/pages/Analytics";
import Settings from "@/pages/Settings";
import Suppliers from "@/pages/Suppliers";
import Team from "@/pages/Team";
import Join from "@/pages/Join";
import Subscription from "@/pages/Subscription";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const isGuest = typeof window !== "undefined" && localStorage.getItem("mag_guest") === "1";
  if (loading) return null;
  if (!user && !isGuest) return <Navigate to="/entra" replace />;
  return children;
}

/**
 * Gating billing. Deve stare SOTTO RequireAuth (già garantisce user loggato o guest).
 * - Guest: passa sempre (demo non gated).
 * - team ancora in fetch: mostra loading, mai redirect a vuoto.
 * - store.subscription_active === true: passa.
 * - false: redirect a /abbonamento.
 *
 * Gestione rientro da Stripe (?checkout=success sulla URL): il backend redirige a
 * /app/impostazioni?checkout=success. Il webhook può arrivare qualche secondo dopo,
 * quindi al primo passaggio subscription_active potrebbe essere ancora false.
 * Facciamo fino a 3 refresh (0s, 2s, 4s) prima di gatare.
 */
function RequirePaid({ children }) {
  const { team, loading, refreshTeam } = useAuth();
  const [params] = useSearchParams();
  const isGuest = typeof window !== "undefined" && localStorage.getItem("mag_guest") === "1";
  const [checkingReturn, setCheckingReturn] = useState(params.get("checkout") === "success");

  useEffect(() => {
    if (params.get("checkout") !== "success") return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 3;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try { await refreshTeam(); } catch { /* noop */ }
      // Rileggo team dallo state dopo il refresh: usiamo un piccolo delay per lasciare
      // che React aggiorni. Se ancora non attivo e abbiamo tentativi residui, riprova.
      setTimeout(() => {
        if (cancelled) return;
        // Non abbiamo team qui in closure (stato); usiamo timeout esterno.
        if (attempts < maxAttempts) {
          setTimeout(tick, 2000);
        } else {
          setCheckingReturn(false);
          toast.success("Abbonamento attivato", {
            description: "Benvenuto in InPalmo. Buon lavoro!",
          });
        }
      }, 200);
    };
    tick();
    return () => { cancelled = true; };
  }, []);

  // Se lo stato diventa attivo prima della fine dei tentativi, esci dalla modalità checking
  useEffect(() => {
    if (checkingReturn && team?.store?.subscription_active === true) {
      setCheckingReturn(false);
      toast.success("Abbonamento attivato", {
        description: "Benvenuto in InPalmo. Buon lavoro!",
      });
    }
  }, [checkingReturn, team?.store?.subscription_active]);

  if (isGuest) return children;
  if (loading || team === null) return null;
  if (checkingReturn) return null; // spinner globale del layout basta, non stampiamo nulla

  const active = team?.store?.subscription_active === true;
  if (active) return children;
  return <Navigate to="/abbonamento" replace />;
}

function AppRouter() {
  const location = useLocation();
  // Auth callback: detect session_id synchronously before any other routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/entra" element={<GuestLogin />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/termini" element={<Termini />} />
      <Route
        path="/abbonamento"
        element={
          <RequireAuth>
            <Subscription />
          </RequireAuth>
        }
      />
      <Route
        path="/app"
        element={
          <RequireAuth>
            <RequirePaid>
              <AppLayout />
            </RequirePaid>
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="prodotti" element={<Products />} />
        <Route path="vendite" element={<Sales />} />
        <Route path="analisi" element={<Analytics />} />
        <Route path="fornitori" element={<Suppliers />} />
        <Route path="impostazioni" element={<Settings />} />
        <Route path="team" element={<Team />} />
        <Route path="team/join" element={<Join />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    document.title = "InPalmo — Il magazzino, in palmo di mano.";
  }, []);

  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <CookieBanner />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
      <Toaster position="top-right" richColors />
    </div>
  );
}
